import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { AlertComponent, DialogComponent, LoadingComponent } from '@components';
import { AcknowledgementOfDebt, Expense, User, UserExpense } from '@models';
import { Store } from '@ngrx/store';
import { UtilService } from '@services';
import { fetchCurrentUserExpenses } from '@state';
import {
  createUserExpense,
  createUserExpenseFailed,
  createUserExpenseSuccess,
  deleteUserExpense,
  deleteUserExpenseFailed,
  deleteUserExpenseSuccess,
  fetchExpenses,
  selectCurrentUserExpenses,
  selectExpenses,
  selectExpensesFetchedAt,
} from '@state/expense';
import { AcknowledgementOfDebtComponent } from './components/acknowledgement-of-debt/acknowledgement-of-debt';

@Component({
  selector: 'app-user-expenses',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    LoadingComponent,
    AlertComponent,
  ],
  templateUrl: './expenses.component.html',
  styleUrl: './expenses.component.scss',
})
export class UserExpensesComponent implements OnInit, OnDestroy {
  @Input() user: User | null = null;

  public $expenses = this.store.select(selectExpenses);
  public $fetched = this.store.select(selectExpensesFetchedAt);
  public loadingExpenseId = '';
  public error: string | null = null;

  private userExpenses: UserExpense[] = [];
  private subscription: Subscription | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private utils: UtilService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fetchExpenses());
    this.store.dispatch(fetchCurrentUserExpenses());

    this.subscription = this.store
      .select(selectCurrentUserExpenses)
      .subscribe((userExpenses) => {
        this.userExpenses = userExpenses;
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  async onToggleExpense(expense: Expense): Promise<void> {
    this.loadingExpenseId = expense.id ?? '';
    this.error = '';

    if (this.doesUserHaveExpense(expense)) {
      if (expense.isAcknowledgementOfDebt) {
        this.openAreYourSureDialog(expense);
      } else {
        await this.deleteUserExpense(expense);
      }
    } else {
      if (expense.isAcknowledgementOfDebt) {
        this.openAodDialog(expense);
      } else {
        await this.addUserExpense(expense);
      }
    }

    this.loadingExpenseId = '';
  }

  private async deleteUserExpense(expense: Expense): Promise<void> {
    try {
      await this.utils.actionsToPromise(
        deleteUserExpense({ userId: this.user!.id!, expenseId: expense.id! }),
        deleteUserExpenseSuccess,
        deleteUserExpenseFailed
      );
    } catch (err) {
      this.error = this.utils.getErrorMessage(err);
    }
  }

  private async addUserExpense(
    expense: Expense,
    acknowledgementOfDebt?: AcknowledgementOfDebt,
    file?: File
  ): Promise<void> {
    try {
      await this.utils.actionsToPromise(
        createUserExpense({
          userId: this.user!.id!,
          expenseId: expense.id!,
          acknowledgementOfDebt,
          file,
        }),
        createUserExpenseSuccess,
        createUserExpenseFailed
      );
    } catch (err) {
      this.error = this.utils.getErrorMessage(err);
    }
  }

  public doesUserHaveExpense(expense: Expense): boolean {
    return this.userExpenses.some((x) => x.expenseId === expense.id);
  }

  public getUserExpense(expense: Expense): UserExpense | undefined {
    return this.userExpenses.find((x) => x.expenseId === expense.id);
  }

  public onNavToExpensesClicked(): void {
    this.router.navigate(['expenses']);
  }

  public openAodDialog(expense: Expense) {
    const dialogRef = this.dialog.open(AcknowledgementOfDebtComponent, {
      data: {
        expense: expense,
        acknowledgementOfDebt:
          this.getUserExpense(expense)?.acknowledgementOfDebt,
        viewOnly: this.doesUserHaveExpense(expense),
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.loadingExpenseId = expense.id ?? '';
        await this.addUserExpense(
          expense,
          result.acknowledgementOfDebt,
          result.file
        );
        this.loadingExpenseId = '';
      }
      this.store.dispatch(fetchCurrentUserExpenses());
    });
  }

  private openAreYourSureDialog(expense: Expense): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        title: 'Are you sure?',
        message:
          'This expense has an acknowledgement of debt. Do you want to proceed?',
        cancel: true,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.loadingExpenseId = expense.id ?? '';
        await this.deleteUserExpense(expense);
        this.loadingExpenseId = '';
      }
      this.store.dispatch(fetchCurrentUserExpenses());
    });
  }
}
